import {
  PREVIEW_FRESHNESS,
  type CopyFieldRole,
  type MarketplaceId,
  type RequirementLevel,
} from "@nova-ai/shared"
import {
  PRODUCT_VALUES_SCHEMA_STATE,
  PRODUCT_VALUES_INSPECTION_STATE,
  type ProductDetailValue,
  type ProductValuesInspection,
  type ProductOverview,
  type ProductValueChecklistAttribute,
  type ProductValueCopywritingField,
  type ProductValuesDetail,
} from "@nova-ai/product-detail-contract"
import { ChevronDown } from "lucide-react"

import { humanizeMarketplaceId } from "../../lib/marketplace"
import { cn } from "../../lib/utils"
import { MarketplaceMark } from "../marketplaces/marketplace-mark"
import { REQUIREMENT_STYLES } from "../marketplaces/requirement-badge"
import { Callout } from "../ui/callout"
import { RichTextValue } from "../ui/rich-text-value"
import { StatusBadge } from "../ui/status-badge"
import { ProductMarketplaceDetails } from "./product-marketplace-details"

type ProductValuesViewProps = {
  detail: ProductValuesDetail
  marketplaceOverview: ProductOverview["marketplaces"][number] | undefined
  onMarketplaceChange: (marketplace: MarketplaceId) => void
}

const REQUIREMENT_ORDER = ["required", "recommended", "optional", "not_needed"] as const

const COPY_ROLE_LABELS = {
  title: "Title",
  shortDescription: "Short description",
  longDescription: "Long description",
  composition: "Composition",
  useRestriction: "Use restriction",
  storageAdvice: "Storage advice",
  sportCategory: "Sport category",
} as const satisfies Record<CopyFieldRole, string>

export function ProductValuesView({
  detail,
  marketplaceOverview,
  onMarketplaceChange,
}: ProductValuesViewProps) {
  if (detail.marketplaces.length === 0 || detail.selectedMarketplace === null) {
    return (
      <Callout tone="info" title="No marketplace checklist">
        This catalog product is not tracked by a marketplace yet.
      </Callout>
    )
  }

  const applicableAttributeCount =
    detail.attributes.length +
    detail.copywriting.filter((field) => field.requirement !== null).length

  return (
    <section className="space-y-8">
      <div role="group" aria-label="Marketplace" className="flex flex-wrap items-center gap-2">
        {detail.marketplaces.map((marketplace) => {
          const isActive = marketplace === detail.selectedMarketplace
          return (
            <button
              key={marketplace}
              type="button"
              aria-pressed={isActive}
              onClick={() => onMarketplaceChange(marketplace)}
              className={cn(
                "inline-flex items-center gap-2 rounded-full border px-2.5 py-1.5 text-[13px] font-medium outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring",
                isActive
                  ? "border-border bg-card text-foreground shadow-card"
                  : "border-transparent bg-muted text-muted-foreground hover:text-foreground"
              )}
            >
              <MarketplaceMark marketplace={marketplace} />

              {humanizeMarketplaceId(marketplace)}
            </button>
          )
        })}
      </div>

      {marketplaceOverview ? (
        <ProductMarketplaceDetails
          key={marketplaceOverview.marketplace}
          entry={marketplaceOverview}
        />
      ) : null}

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-[15px] font-semibold tracking-tight text-foreground">
            Marketplace content
          </h2>

          <p className="mt-1 text-sm text-foreground">
            {detail.category?.label ?? "Category label unavailable"}
          </p>

          {detail.category ? (
            <p className="mt-0.5 font-mono text-xs text-muted-foreground">
              Category code {detail.category.code}
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-muted-foreground">Marketplace category unavailable</p>
          )}
        </div>

        {detail.schemaState === PRODUCT_VALUES_SCHEMA_STATE.available ? (
          <span className="text-xs text-muted-foreground tabular-nums">
            {applicableAttributeCount.toLocaleString()} applicable attributes
          </span>
        ) : null}
      </div>

      {detail.schemaState !== PRODUCT_VALUES_SCHEMA_STATE.available ? (
        <Callout
          tone={
            detail.schemaState === PRODUCT_VALUES_SCHEMA_STATE.missingCategory ? "warning" : "info"
          }
          title={
            detail.schemaState === PRODUCT_VALUES_SCHEMA_STATE.missingCategory
              ? "Marketplace category missing"
              : "Marketplace schema unavailable"
          }
        >
          {detail.schemaMessage ?? "The attribute checklist is unavailable."}
        </Callout>
      ) : null}

      <ValueInspectionNotice inspection={detail.inspection} />

      {detail.inspection.kind === PRODUCT_VALUES_INSPECTION_STATE.available ? (
        <StoredValueSections detail={detail} />
      ) : null}
    </section>
  )
}

function StoredValueSections({ detail }: { detail: ProductValuesDetail }) {
  return (
    <>
      <CopywritingSection fields={detail.copywriting} />

      {detail.attributes.length > 0 ? <AttributesByRequirement rows={detail.attributes} /> : null}

      {detail.offSchema.length > 0 ? (
        <section className="space-y-3" aria-labelledby="off-schema-heading">
          <div>
            <h2 id="off-schema-heading" className="text-[15px] font-semibold text-foreground">
              {detail.schemaState === PRODUCT_VALUES_SCHEMA_STATE.available
                ? "Stored values outside this schema"
                : "Stored values"}
            </h2>

            <p className="mt-1 text-xs text-muted-foreground">
              These values remain stored and visible. They are not part of the current checklist.
            </p>
          </div>

          <AttributeRows rows={detail.offSchema} />
        </section>
      ) : null}
    </>
  )
}

function ValueInspectionNotice({ inspection }: { inspection: ProductValuesInspection }) {
  if (inspection.kind === PRODUCT_VALUES_INSPECTION_STATE.unavailable) {
    return (
      <Callout tone="info" title="Saved values unavailable">
        {inspection.reason}
      </Callout>
    )
  }
  return (
    <>
      {inspection.freshness.kind === PREVIEW_FRESHNESS.historical ? (
        <Callout tone="warning" title="Historical saved values">
          These saved values are not current. {inspection.freshness.recovery.message}
        </Callout>
      ) : null}

      {inspection.kind === PRODUCT_VALUES_INSPECTION_STATE.excluded ? (
        <Callout tone="info" title="Product excluded from marketplace values">
          {inspection.reason}
        </Callout>
      ) : null}
    </>
  )
}

function CopywritingSection({ fields }: { fields: readonly ProductValueCopywritingField[] }) {
  return (
    <section className="space-y-3" aria-labelledby="copywriting-heading">
      <div>
        <h2 id="copywriting-heading" className="text-[15px] font-semibold text-foreground">
          Copywriting
        </h2>

        <p className="mt-1 text-xs text-muted-foreground">
          Storefront text prepared for this marketplace.
        </p>
      </div>

      {fields.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-card py-8 text-center text-sm text-muted-foreground">
          This marketplace has no copywriting fields configured.
        </div>
      ) : (
        <div className="divide-y divide-border/70 rounded-xl border border-border bg-card shadow-card">
          {fields.map((field) => (
            <CopywritingPreview key={field.attrCode} field={field} />
          ))}
        </div>
      )}
    </section>
  )
}

function CopywritingPreview({ field }: { field: ProductValueCopywritingField }) {
  const configValue = visibleValue(field.values.config)
  const variants = visibleVariants(field)
  const roleLabel = field.roles.map((role) => COPY_ROLE_LABELS[role]).join(" / ")

  return (
    <article className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <AttributeIdentity
          label={roleLabel}
          attrCode={field.attrCode}
          metadataLabel={field.label}
        />

        {!configValue && variants.length === 0 ? (
          <EmptyValueStatus requirement={field.requirement} />
        ) : null}
      </div>

      {configValue ? (
        <div
          className={cn(
            "mt-3 text-sm leading-6",
            field.roles.includes("longDescription") &&
              "max-h-96 overflow-y-auto rounded-lg bg-muted/25 p-3"
          )}
        >
          {field.format === "plain" ? (
            <p className="whitespace-pre-wrap break-words text-foreground">{configValue}</p>
          ) : (
            <RichTextValue value={configValue} format={field.format} />
          )}
        </div>
      ) : null}

      {configValue ? (
        <p className="mt-2 text-xs text-muted-foreground tabular-nums">
          {configValue.length.toLocaleString()} characters
        </p>
      ) : null}

      <VariantValuesDisclosure variants={variants} />
    </article>
  )
}

function AttributesByRequirement({ rows }: { rows: readonly ProductValueChecklistAttribute[] }) {
  return (
    <section className="space-y-5" aria-labelledby="attributes-heading">
      <div>
        <h2 id="attributes-heading" className="text-[15px] font-semibold text-foreground">
          Attributes
        </h2>

        <p className="mt-1 text-xs text-muted-foreground">
          Grouped by marketplace importance. Variant values stay collapsed until needed.
        </p>
      </div>

      {REQUIREMENT_ORDER.map((requirement) => {
        const group = rows.filter((row) => row.requirement === requirement)
        return group.length > 0 ? (
          <RequirementGroup key={requirement} requirement={requirement} rows={group} />
        ) : null
      })}
    </section>
  )
}

function RequirementGroup({
  requirement,
  rows,
}: {
  requirement: RequirementLevel
  rows: readonly ProductValueChecklistAttribute[]
}) {
  const style = REQUIREMENT_STYLES[requirement]
  return (
    <section className="space-y-2" aria-labelledby={`requirement-${requirement}`}>
      <h3
        id={`requirement-${requirement}`}
        className="flex items-center gap-2 text-sm font-medium text-foreground"
      >
        <span className={cn("size-1.5 rounded-full", style.dot)} />

        {`${style.label} · ${rows.length.toLocaleString()}`}
      </h3>

      <AttributeRows rows={rows} />
    </section>
  )
}

function AttributeRows({ rows }: { rows: readonly ProductValueChecklistAttribute[] }) {
  return (
    <div className="divide-y divide-border/70 rounded-xl border border-border bg-card shadow-card">
      {rows.map((attribute) => (
        <AttributeRow key={attribute.attrCode} attribute={attribute} />
      ))}
    </div>
  )
}

function AttributeRow({ attribute }: { attribute: ProductValueChecklistAttribute }) {
  const configValue = visibleValue(attribute.values.config)
  const variants = visibleVariants(attribute)
  return (
    <div className="p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <AttributeIdentity
          label={readableAttributeLabel(attribute)}
          attrCode={attribute.attrCode}
        />

        {!configValue && variants.length === 0 ? (
          <EmptyValueStatus requirement={attribute.requirement} />
        ) : null}
      </div>

      {configValue ? (
        <p className="mt-2 whitespace-pre-wrap break-words text-sm text-foreground">
          {configValue}
        </p>
      ) : null}

      <VariantValuesDisclosure variants={variants} />
    </div>
  )
}

function EmptyValueStatus({ requirement }: { requirement: RequirementLevel | null }) {
  return requirement === "not_needed" ? (
    <StatusBadge tone="muted" size="sm">
      Not set
    </StatusBadge>
  ) : (
    <StatusBadge tone="warning" size="sm">
      Missing
    </StatusBadge>
  )
}

function AttributeIdentity({
  label,
  attrCode,
  metadataLabel,
}: {
  label: string
  attrCode: string
  metadataLabel?: string | null
}) {
  return (
    <div className="min-w-0">
      <p className="text-sm font-medium text-foreground">{label}</p>

      {metadataLabel && metadataLabel !== label ? (
        <p className="mt-0.5 text-xs text-muted-foreground">{metadataLabel}</p>
      ) : null}

      <p className="mt-0.5 break-all font-mono text-[11px] text-muted-foreground">{attrCode}</p>
    </div>
  )
}

type VisibleVariant = { simpleSku: string; value: string }

function VariantValuesDisclosure({ variants }: { variants: readonly VisibleVariant[] }) {
  if (variants.length === 0) {
    return null
  }
  const countLabel = `${variants.length.toLocaleString()} variant ${variants.length === 1 ? "value" : "values"}`
  return (
    <details className="group/variants mt-3 rounded-lg bg-muted/30">
      <summary className="flex cursor-pointer list-none items-center justify-between gap-3 rounded-lg px-3 py-2 text-xs font-medium text-foreground outline-none hover:bg-muted/55 focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-details-marker]:hidden">
        {countLabel}

        <ChevronDown className="size-3.5 shrink-0 transition-transform group-open/variants:rotate-180 motion-reduce:transition-none" />
      </summary>

      <div className="overflow-x-auto border-t border-border/60">
        <table className="min-w-full text-xs">
          <thead className="text-left text-muted-foreground">
            <tr>
              <th className="px-3 py-2 font-medium">Simple SKU</th>

              <th className="px-3 py-2 font-medium">Value</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-border/60">
            {variants.map((variant) => (
              <tr key={variant.simpleSku}>
                <td className="whitespace-nowrap px-3 py-2 align-top font-mono text-muted-foreground">
                  {variant.simpleSku}
                </td>

                <td className="min-w-64 whitespace-pre-wrap break-words px-3 py-2 align-top text-foreground">
                  {variant.value}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </details>
  )
}

function visibleVariants(attribute: ProductValueChecklistAttribute): VisibleVariant[] {
  return attribute.values.variants.flatMap((variant) => {
    const value = visibleValue(variant.value)
    return value ? [{ simpleSku: variant.simpleSku, value }] : []
  })
}

function readableAttributeLabel(attribute: ProductValueChecklistAttribute): string {
  const label = attribute.label?.trim()
  if (label) {
    return label
  }
  const humanized = attribute.attrCode
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .trim()
  if (/[A-Za-z]/.test(humanized)) {
    return humanized.charAt(0).toUpperCase() + humanized.slice(1)
  }
  return "Label unavailable"
}

function visibleValue(value: ProductDetailValue | null): string | null {
  const trimmed = value?.value.trim() ?? ""
  return trimmed.length > 0 ? (value?.value ?? null) : null
}
